/**
 * @enum {string}
 */
export const LocalKey = {
  USER: 'user',
};

/**
 * @enum {string}
 */
export const SessionKey = {
  PROVIDER_CONFIGS: 'providerConfigs',
  AWAITING_SIGN_IN: 'awaitingSignIn',
  AWAITING_RENEW: 'awaitingRenew',
  RETURN_TO: 'returnTo',
  STATE: 'state',
  NONCE: 'nonce',
  CODE_VERIFIER: 'codeVerifier',
};

export class Store {
  /**
   * @type {string}
   */
  #key;

  /**
   * @type {Storage}
   */
  #storage;

  /**
   * @param {string} key The key to use for storage.
   * @param {Storage} storage The storage to use.
   */
  constructor(key, storage) {
    this.#key = key;
    this.#storage = storage;
  }

  /**
   * Get all of the data associated with this store.
   * @return {Object<string, unknown>} The data.
   */
  #getAll() {
    const data = this.#storage.getItem(this.#key);
    return data ? JSON.parse(data) : {};
  }

  /**
   * Set all of the data associated with this store.
   * @param {Object} data The data.
   */
  #setAll(data) {
    this.#storage.setItem(this.#key, JSON.stringify(data));
  }

  /**
   * Store a value.
   * @param {string} key The key.
   * @param {any} value The value.
   */
  set(key, value) {
    const data = this.#getAll();
    data[key] = value;
    this.#setAll(data);
  }

  /**
   * Get a value.
   * @param {string} key The key.
   * @return {any} The value.
   */
  get(key) {
    const data = this.#getAll();
    return data[key];
  }

  /**
   * Get a value and remove it from the store.
   * @param {string} key The key.
   * @return {any} The value.
   */
  extract(key) {
    const data = this.#getAll();
    if (!data.hasOwnProperty(key)) {
      return undefined;
    }
    const value = data[key];
    delete data[key];
    this.#setAll(data);
    return value;
  }

  /**
   * Delete a value.
   * @param {string} key The key.
   */
  delete(key) {
    const data = this.#getAll();
    if (!data.hasOwnProperty(key)) {
      return;
    }
    delete data[key];
    this.#setAll(data);
  }

  /**
   * Clear all values.
   */
  clear() {
    this.#storage.removeItem(this.#key);
  }
}
